import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  BookOpen,
  ChevronRight,
  Loader2,
  GraduationCap,
  Plus,
  Users,
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useLanguage } from "@/hooks/use-language";
import { useMyExams } from "@/hooks/use-exams";
import { useMyGroups } from "@/hooks/use-groups";
import { cleanExamText } from "@/lib/exam-display";
import { createErrorMessage } from "@/lib/error-message";
import { ExamInquiryCard } from "@/components/exam-inquiry-card";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

export const Route = createFileRoute("/_main/dashboard")({
  component: DashboardPage,
});

function DashboardPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { t } = useLanguage();

  const {
    data: exams = [],
    isLoading: examsLoading,
    error: examsError,
  } = useMyExams();
  const { data: groups = [], isLoading: groupsLoading } = useMyGroups();

  // "Group exams" — every slug surfaced through any of my groups. The
  // dashboard shows groups as the primary entry point and only shows
  // *standalone* exams (granted outside any group) as a fallback list,
  // so existing pre-group access doesn't disappear.
  const groupExamSlugs = new Set<string>();
  for (const g of groups) for (const slug of g.exams) groupExamSlugs.add(slug);
  const standaloneExams = exams.filter((e) => !groupExamSlugs.has(e.id));

  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  const loading = examsLoading || groupsLoading;
  const hasNothing =
    !loading && groups.length === 0 && standaloneExams.length === 0;

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950">
      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
        {/* Welcome */}
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900 dark:text-zinc-50 sm:text-3xl">
              {t("dashboard.greeting")}
            </h1>
            <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
              {user?.email}
            </p>
          </div>
          <button
            onClick={() => navigate({ to: "/request-exam" })}
            className="inline-flex cursor-pointer items-center gap-2 self-start rounded-lg border border-zinc-200 bg-white px-4 py-2 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800"
          >
            <Plus className="size-4" />
            {t("dashboard.request_exam_cta")}
          </button>
        </div>

        {examsError && (
          <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-center text-sm text-red-600 dark:border-red-800 dark:bg-red-950 dark:text-red-400">
            {t("dashboard.load_error")}: {createErrorMessage(examsError)}
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[...Array(3)].map((_, i) => (
              <Card key={i}>
                <CardContent className="py-6">
                  <div className="animate-pulse space-y-3">
                    <div className="h-5 w-40 rounded bg-zinc-200 dark:bg-zinc-700" />
                    <div className="h-4 w-full rounded bg-zinc-100 dark:bg-zinc-800" />
                    <div className="h-4 w-24 rounded bg-zinc-100 dark:bg-zinc-800" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : hasNothing ? (
          <>
            <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-zinc-200 py-20 text-center text-zinc-400 dark:border-zinc-800">
              <GraduationCap className="mb-4 h-12 w-12" />
              <p className="text-lg font-medium">아직 가입한 그룹이 없습니다</p>
              <p className="mt-1 max-w-sm text-sm">
                그룹은 초대로만 가입할 수 있어요. 운영자에게 초대를 요청하거나,
                새 시험 추가가 필요하면 아래 카드로 문의해주세요.
              </p>
            </div>
            <div className="mt-6">
              <ExamInquiryCard />
            </div>
          </>
        ) : (
          <>
            {/* My groups */}
            {groups.length > 0 && (
              <section className="mb-10">
                <div className="mb-3 flex items-center gap-2">
                  <Users className="size-5 text-zinc-500" />
                  <h2 className="text-lg font-semibold">내 그룹</h2>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {groups.map((g) => (
                    <Link
                      key={g.id}
                      to="/groups/$gid"
                      params={{ gid: g.id }}
                      className="block"
                    >
                      <Card className="h-full transition-all hover:border-blue-300 hover:shadow-lg dark:hover:border-blue-700">
                        <CardHeader className="pb-3">
                          <div className="flex items-start justify-between">
                            <div className="rounded-lg bg-purple-50 p-2.5 dark:bg-purple-950">
                              <Users className="h-5 w-5 text-purple-500" />
                            </div>
                            <ChevronRight className="h-5 w-5 text-zinc-300" />
                          </div>
                          <CardTitle className="mt-3 text-lg line-clamp-1">
                            {g.name}
                          </CardTitle>
                          {g.description && (
                            <CardDescription className="line-clamp-2">
                              {g.description}
                            </CardDescription>
                          )}
                        </CardHeader>
                      </Card>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* Standalone exams (granted outside any group) */}
            {standaloneExams.length > 0 && (
              <section className="mb-10">
                <div className="mb-3 flex items-center gap-2">
                  <BookOpen className="size-5 text-zinc-500" />
                  <h2 className="text-lg font-semibold">개인 시험</h2>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {standaloneExams.map((exam) => (
                    <Card
                      key={exam.id}
                      className="cursor-pointer transition-all hover:shadow-lg hover:border-blue-300 dark:hover:border-blue-700"
                      onClick={() =>
                        navigate({
                          to: "/exams/$slug",
                          params: { slug: exam.id },
                        })
                      }
                    >
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between">
                          <div className="rounded-lg bg-blue-50 p-2.5 dark:bg-blue-950">
                            <BookOpen className="h-5 w-5 text-blue-500" />
                          </div>
                          <ChevronRight className="h-5 w-5 text-zinc-300" />
                        </div>
                        <CardTitle className="mt-3 text-lg">
                          {cleanExamText(exam.name)}
                        </CardTitle>
                        <CardDescription>
                          {cleanExamText(exam.description)}
                        </CardDescription>
                      </CardHeader>
                    </Card>
                  ))}
                </div>
              </section>
            )}

            <div className="mt-8">
              <ExamInquiryCard />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
